import React, { useState, useEffect } from "react";
import API from "../config/api";
import "../styles/Account.css";

function OpinionPollSettings() {
  const [poll, setPoll] = useState({
    questionTa: "",
    questionEn: "",
    option1Ta: "",
    option1En: "",
    option2Ta: "",
    option2En: "",
    image: "",
  });

  const [liveStats, setLiveStats] = useState({
    votes1Ta: 0,
    votes2Ta: 0,
    votes1En: 0,
    votes2En: 0,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // 1. Initial load to populate form inputs and live stats
    const loadInitialPoll = async () => {
      try {
        setLoading(true);
        const res = await API.get("/api/opinion-poll/admin");
        if (res.data?.success && res.data.poll) {
          const data = res.data.poll;
          setPoll({
            questionTa: data.questionTa || "",
            questionEn: data.questionEn || "",
            option1Ta: data.option1Ta || "",
            option1En: data.option1En || "",
            option2Ta: data.option2Ta || "",
            option2En: data.option2En || "",
            image: data.image || "",
          });
          setLiveStats({
            votes1Ta: data.votes1Ta || 0,
            votes2Ta: data.votes2Ta || 0,
            votes1En: data.votes1En || 0,
            votes2En: data.votes2En || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching initial opinion poll:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialPoll();

    // 2. Background timer for live stats ONLY (NEVER touches form inputs)
    const interval = setInterval(async () => {
      try {
        const res = await API.get("/api/opinion-poll/admin");
        if (res.data?.success && res.data.poll) {
          const data = res.data.poll;
          setLiveStats({
            votes1Ta: data.votes1Ta || 0,
            votes2Ta: data.votes2Ta || 0,
            votes1En: data.votes1En || 0,
            votes2En: data.votes2En || 0,
          });
        }
      } catch (err) {}
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    setPoll((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleUploadImage = async () => {
    if (!imageFile) return poll.image;
    try {
      setUploading(true);
      const data = new FormData();
      data.append("image", imageFile);

      const res = await API.post("/api/ads/upload", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data.success) {
        return res.data.url;
      }
      throw new Error(res.data.message || "Upload failed");
    } catch (err) {
      console.error("Image upload failed", err);
      alert("Image upload failed. Please try again.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const confirmReset = window.confirm(
      "Are you sure you want to save this poll? Saving a new poll will reset all current vote counts to 0."
    );
    if (!confirmReset) return;

    try {
      setSaving(true);
      let uploadedUrl = poll.image;
      if (imageFile) {
        uploadedUrl = await handleUploadImage();
        if (!uploadedUrl) {
          setSaving(false);
          return;
        }
      }

      const res = await API.post("/api/opinion-poll/admin", {
        ...poll,
        image: uploadedUrl
      });
      if (res.data?.success) {
        alert("Opinion poll updated and reset successfully! 📊");
        setImageFile(null);
        if (res.data.poll) {
          const d = res.data.poll;
          setPoll({
            questionTa: d.questionTa || "",
            questionEn: d.questionEn || "",
            option1Ta: d.option1Ta || "",
            option1En: d.option1En || "",
            option2Ta: d.option2Ta || "",
            option2En: d.option2En || "",
            image: d.image || ""
          });
          setLiveStats({
            votes1Ta: d.votes1Ta || 0,
            votes2Ta: d.votes2Ta || 0,
            votes1En: d.votes1En || 0,
            votes2En: d.votes2En || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error updating opinion poll:", error);
      alert(error.response?.data?.message || "Failed to update opinion poll ❌");
    } finally {
      setSaving(false);
    }
  };

  // Tamil Portal Stats
  const v1Ta = liveStats.votes1Ta || 0;
  const v2Ta = liveStats.votes2Ta || 0;
  const totalTa = v1Ta + v2Ta;
  const pct1Ta = totalTa > 0 ? Math.round((v1Ta / totalTa) * 100) : 0;
  const pct2Ta = totalTa > 0 ? 100 - pct1Ta : 0;

  // English Portal Stats
  const v1En = liveStats.votes1En || 0;
  const v2En = liveStats.votes2En || 0;
  const totalEn = v1En + v2En;
  const pct1En = totalEn > 0 ? Math.round((v1En / totalEn) * 100) : 0;
  const pct2En = totalEn > 0 ? 100 - pct1En : 0;

  // Global total
  const totalGlobal = totalTa + totalEn;

  return (
    <div className="account-page">
      <div className="account-header">
        <h1 className="account-title">Opinion Poll Settings</h1>
        <p className="account-subtitle">Configure the active homepage opinion poll and monitor live vote results</p>
      </div>

      <div className="account-sections">
        {/* CURRENT RESULTS SIDE CARD */}
        <div className="account-card" style={{ flex: "1", minWidth: "300px" }}>
          <h2>Live Poll Statistics</h2>
          <p className="card-desc">Real-time statistics of the active opinion poll</p>
          
          <div style={{ marginTop: "20px" }}>
            <div style={{ marginBottom: "20px", padding: "15px", background: "rgba(255,255,255,0.03)", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
              <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>COMBINED TOTAL VOTES</span>
              <strong style={{ fontSize: "24px", color: "var(--accent-orange)" }}>{totalGlobal}</strong>
            </div>

            {/* TAMIL PORTAL STATS */}
            <div style={{ marginBottom: "25px", borderBottom: "1px solid var(--border-color)", paddingBottom: "15px" }}>
              <h3 style={{ fontSize: "14px", color: "var(--accent-orange)", marginBottom: "10px" }}>Tamil Portal ({totalTa} votes)</h3>
              
              <div style={{ marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px" }}>
                  <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "160px" }}>Option 1 ({v1Ta})</span>
                  <strong>{pct1Ta}%</strong>
                </div>
                <div style={{ width: "100%", height: "8px", background: "var(--border-color)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: `${pct1Ta}%`, height: "100%", background: "var(--accent-orange)" }}></div>
                </div>
              </div>

              <div style={{ marginBottom: "5px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px" }}>
                  <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "160px" }}>Option 2 ({v2Ta})</span>
                  <strong>{pct2Ta}%</strong>
                </div>
                <div style={{ width: "100%", height: "8px", background: "var(--border-color)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: `${pct2Ta}%`, height: "100%", background: "var(--accent-orange)" }}></div>
                </div>
              </div>
            </div>

            {/* ENGLISH PORTAL STATS */}
            <div style={{ marginBottom: "10px" }}>
              <h3 style={{ fontSize: "14px", color: "var(--accent-orange)", marginBottom: "10px" }}>English Portal ({totalEn} votes)</h3>
              
              <div style={{ marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px" }}>
                  <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "160px" }}>Option 1 ({v1En})</span>
                  <strong>{pct1En}%</strong>
                </div>
                <div style={{ width: "100%", height: "8px", background: "var(--border-color)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: `${pct1En}%`, height: "100%", background: "var(--accent-orange)" }}></div>
                </div>
              </div>

              <div style={{ marginBottom: "5px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "13px" }}>
                  <span style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", maxWidth: "160px" }}>Option 2 ({v2En})</span>
                  <strong>{pct2En}%</strong>
                </div>
                <div style={{ width: "100%", height: "8px", background: "var(--border-color)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ width: `${pct2En}%`, height: "100%", background: "var(--accent-orange)" }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EDIT POLL CONFIGURATION FORM */}
        <div className="account-card" style={{ flex: "2", minWidth: "400px" }}>
          <h2>Configure Opinion Poll</h2>
          <p className="card-desc">
            Edit the question and options for both the English and Tamil portals.
          </p>

          {loading ? (
            <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>Loading settings...</div>
          ) : (
            <form onSubmit={handleSubmit} className="account-form">
              {/* IMAGE UPLOAD SECTION */}
              <div style={{ marginBottom: "25px", borderBottom: "1px solid var(--border-color)", paddingBottom: "20px" }}>
                <h3 style={{ color: "var(--accent-orange)", marginBottom: "12px" }}>Opinion Poll Image Banner</h3>
                <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                  {(imageFile || poll.image) && (
                    <div style={{ position: "relative", width: "150px", height: "90px", border: "1px solid var(--border-color)", borderRadius: "6px", overflow: "hidden" }}>
                      <img 
                        src={imageFile ? URL.createObjectURL(imageFile) : (poll.image.startsWith("http") ? poll.image : API.defaults.baseURL + poll.image)} 
                        alt="Preview" 
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                      />
                    </div>
                  )}
                  <div className="form-group" style={{ flex: 1, margin: 0 }}>
                    <label>Upload Banner Image</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ padding: "8px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border-color)", borderRadius: "4px", width: "100%", color: "var(--text-primary)" }}
                    />
                    {imageFile && <p style={{ fontSize: "11px", color: "var(--accent-orange)", marginTop: "4px" }}>Selected file: {imageFile.name}</p>}
                    {uploading && <p style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>Uploading image...</p>}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                
                {/* TAMIL CONFIGURATION */}
                <div>
                  <h3 style={{ borderBottom: "1px solid var(--border-color)", paddingBottom: "8px", marginBottom: "15px", color: "var(--accent-orange)" }}>Tamil Portal</h3>
                  
                  <div className="form-group">
                    <label>கருத்துக் கணிப்பு கேள்வி</label>
                    <textarea
                      name="questionTa"
                      value={poll.questionTa}
                      onChange={handleChange}
                      placeholder="எ.கா. இணைய வழிக் கல்வி மாணவர்களுக்கு உகந்ததா?"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>பதில் 1</label>
                    <input
                      type="text"
                      name="option1Ta"
                      value={poll.option1Ta}
                      onChange={handleChange}
                      placeholder="எ.கா. ஆம், மிகவும் உகந்தது"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>பதில் 2</label>
                    <input
                      type="text"
                      name="option2Ta"
                      value={poll.option2Ta}
                      onChange={handleChange}
                      placeholder="எ.கா. இல்லை, நேரடி கல்வியே சிறந்தது"
                      required
                    />
                  </div>
                </div>

                {/* ENGLISH CONFIGURATION */}
                <div>
                  <h3 style={{ borderBottom: "1px solid var(--border-color)", paddingBottom: "8px", marginBottom: "15px", color: "var(--accent-orange)" }}>English Portal</h3>

                  <div className="form-group">
                    <label>Poll Question</label>
                    <textarea
                      name="questionEn"
                      value={poll.questionEn}
                      onChange={handleChange}
                      placeholder="e.g. Is online education suitable for students?"
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Option 1</label>
                    <input
                      type="text"
                      name="option1En"
                      value={poll.option1En}
                      onChange={handleChange}
                      placeholder="e.g. Yes, highly suitable"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Option 2</label>
                    <input
                      type="text"
                      name="option2En"
                      value={poll.option2En}
                      onChange={handleChange}
                      placeholder="e.g. No, direct education is better"
                      required
                    />
                  </div>
                </div>

              </div>

              <div style={{ marginTop: "20px", padding: "12px", background: "rgba(239, 68, 68, 0.08)", borderLeft: "4px solid #ef4444", borderRadius: "4px", fontSize: "12px", color: "#f87171" }}>
                <strong>⚠️ Warning:</strong> Saving this form will instantly overwrite the active homepage poll and reset the current vote counts to 0.
              </div>

              <button type="submit" className="save-btn" style={{ marginTop: "20px" }} disabled={saving || uploading}>
                {saving || uploading ? (uploading ? "Uploading Image..." : "Saving...") : "Save and Reset Poll"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default OpinionPollSettings;
